// An access point's plate as a thumbnail that opens it whole (#1889), shared by the two pages that
// draw one (#2001): the device page, where it is the identity card's leading column, and the
// catalogue, where it stands for the card's face. One implementation, so the two cannot drift:
// the dialog, its Close, Escape and a click outside, and the focus handed back to the thumbnail.
// The dialog is the shared modal `Dialog` (UX-002), which holds focus inside it while it is open.
// Each page passes the plate as it draws it; the thumbnail draws nothing of its own but the photo.
import { useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "../../surfaces";

/** An access point's plate as a thumbnail (#1889): its model's photo, or a drawn outline where the
 * catalog holds none. Pressing it opens the whole plate in a dialog. Its box is the stylesheet's:
 * about 104 px on the device page, a catalogue card's width and a bounded height there (#2001). */
export function DeviceThumb({ name, photo, className, children }: { name: string; photo?: { src: string }; className?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  // Focus is handed back to the thumbnail by name, because a press does not focus a button in
  // every browser, and the dialog can only give it back to what held it.
  const close = () => { setOpen(false); button.current?.focus(); };
  return (
    <>
      <button ref={button} type="button" className={"device-thumb" + (className ? ` ${className}` : "")} aria-haspopup="dialog" aria-label={`Show ${name}'s plate`} onClick={() => setOpen(true)}>
        {photo
          ? <img src={photo.src} alt="" />
          : (
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <rect x="16" y="6" width="32" height="52" rx="8" fill="var(--surface-2)" stroke="var(--line-2)" strokeWidth="2" />
              <rect x="22" y="44" width="20" height="4" rx="2" fill="var(--line-2)" />
            </svg>
          )}
      </button>
      {open && <PlateDialog name={name} onClose={close}>{children}</PlateDialog>}
    </>
  );
}

/** The whole plate over the page: a dialog with its own Close, closed by Escape and by a click
 * outside it, which hands the focus back to the thumbnail. */
function PlateDialog({ name, onClose, children }: { name: string; onClose(): void; children: ReactNode }) {
  const closer = useRef<HTMLButtonElement>(null);
  return createPortal(
    <Dialog label={`${name}'s plate`} className="plate-dialog" onClose={onClose} backdropCloses initialFocus={closer}>
      <div className="panel-head">
        <h2>{name}</h2>
        <button ref={closer} type="button" className="btn btn-quiet" aria-label="Close" onClick={onClose}>×</button>
      </div>
      {children}
    </Dialog>,
    document.body,
  );
}
